import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { api, readApiError } from '../../lib/api';
import { formatCop } from '../../lib/format';
import { STATE_COLORS, STATE_LABELS } from '../os/state-machine';
import type { ServiceOrderState } from '@horology/shared-types';
import { hasPermission, useMe } from '../../hooks/useAuth';

interface TimelineDTO {
  customer: {
    id: number;
    idDocument: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string | null;
    city: string | null;
    notes: string | null;
    createdAt: string;
  };
  orders: Array<{
    id: number;
    folio: string;
    state: ServiceOrderState;
    brand: string;
    model: string;
    intakeAt: string;
    deliveredAt: string | null;
    estimatedValue: string;
    finalValue: string | null;
  }>;
  invoices: Array<{
    id: number;
    number: string;
    status: string;
    total: string;
    issuedAt: string;
    serviceOrder: { id: number; folio: string } | null;
  }>;
  totals: {
    orderCount: number;
    deliveredCount: number;
    invoiceCount: number;
    lifetimeSpend: number;
  };
}

export function CustomerDetailPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = Number(params.id);

  const data = useQuery({
    queryKey: ['customer', id, 'timeline'],
    queryFn: async () => (await api.get<TimelineDTO>(`/api/customers/${id}/timeline`)).data,
    enabled: Number.isInteger(id),
  });

  if (data.isLoading) return <p>Cargando...</p>;
  if (data.isError || !data.data) return <p className="error">No se encontró el cliente.</p>;

  const c = data.data.customer;
  const t = data.data.totals;

  // Build timeline entries (mix orders + invoices, sorted desc by date)
  type TimelineEntry =
    | { kind: 'order'; date: string; data: TimelineDTO['orders'][number] }
    | { kind: 'invoice'; date: string; data: TimelineDTO['invoices'][number] };

  const entries: TimelineEntry[] = [
    ...data.data.orders.map((o) => ({ kind: 'order' as const, date: o.intakeAt, data: o })),
    ...data.data.invoices.map((i) => ({ kind: 'invoice' as const, date: i.issuedAt, data: i })),
  ].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <Link to="/customers" className="btn">← Clientes</Link>
        <h1 style={{ margin: 0 }}>
          {c.firstName} {c.lastName}
        </h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Datos de contacto</h3>
          <p style={{ margin: '0.25rem 0' }}><strong>CC/NIT:</strong> {c.idDocument}</p>
          <p style={{ margin: '0.25rem 0' }}><strong>Email:</strong> {c.email}</p>
          <p style={{ margin: '0.25rem 0' }}><strong>Teléfono:</strong> {c.phone}</p>
          {c.address && (
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Dirección:</strong> {c.address} {c.city ? `· ${c.city}` : ''}
            </p>
          )}
          <NotesEditor customerId={c.id} initial={c.notes} />
          <p style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '0.75rem' }}>
            Cliente desde {new Date(c.createdAt).toLocaleDateString('es-CO')}
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.75rem',
          }}
        >
          <KpiBox label="Órdenes totales" value={String(t.orderCount)} />
          <KpiBox label="Entregadas" value={String(t.deliveredCount)} />
          <KpiBox label="Facturas" value={String(t.invoiceCount)} />
          <KpiBox label="Total gastado" value={formatCop(t.lifetimeSpend)} accent />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <h3 style={{ margin: 0, padding: '0.75rem 1rem', background: 'var(--color-bg)' }}>
          Línea de tiempo
        </h3>
        {entries.length === 0 ? (
          <p style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-muted)' }}>
            Sin actividad registrada.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {entries.map((e) => (
              <li
                key={`${e.kind}-${e.data.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  borderTop: '1px solid var(--color-border)',
                }}
              >
                <span
                  style={{
                    width: 36,
                    fontSize: '0.7rem',
                    color: 'var(--color-muted)',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                  }}
                >
                  {e.kind === 'order' ? 'OS' : 'Fac.'}
                </span>
                {e.kind === 'order' ? (
                  <>
                    <Link
                      to="/orders/$id"
                      params={{ id: String(e.data.id) }}
                      style={{ flex: 1 }}
                    >
                      <code>{e.data.folio}</code> · {e.data.brand} {e.data.model}
                    </Link>
                    <span
                      style={{
                        background: STATE_COLORS[e.data.state],
                        color: '#fff',
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: '0.7rem',
                      }}
                    >
                      {STATE_LABELS[e.data.state]}
                    </span>
                    <span style={{ width: 110, textAlign: 'right' }}>
                      {formatCop(e.data.finalValue ?? e.data.estimatedValue)}
                    </span>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1 }}>
                      <code>{e.data.number}</code>
                      {e.data.serviceOrder && (
                        <small style={{ color: 'var(--color-muted)', marginLeft: 8 }}>
                          ← OS {e.data.serviceOrder.folio}
                        </small>
                      )}
                    </div>
                    <span
                      style={{
                        background: e.data.status === 'CANCELLED' ? '#9ca3af' : '#10b981',
                        color: '#fff',
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: '0.7rem',
                      }}
                    >
                      {e.data.status}
                    </span>
                    <span style={{ width: 110, textAlign: 'right' }}>
                      {formatCop(e.data.total)}
                    </span>
                  </>
                )}
                <span
                  style={{
                    width: 100,
                    fontSize: '0.75rem',
                    color: 'var(--color-muted)',
                    textAlign: 'right',
                  }}
                >
                  {new Date(e.date).toLocaleDateString('es-CO')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function KpiBox({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="card" style={{ borderLeft: accent ? '4px solid var(--color-accent)' : undefined }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--color-muted)' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function NotesEditor({ customerId, initial }: { customerId: number; initial: string | null }) {
  const me = useMe();
  const canEdit = hasPermission(me.data, 'os.create');
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initial ?? '');
  }, [initial]);

  const saveMut = useMutation({
    mutationFn: async (notes: string) => {
      await api.patch(`/api/customers/${customerId}`, { notes: notes || null });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['customer', customerId, 'timeline'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (e) => setError(readApiError(e).message),
  });

  if (!editing) {
    return (
      <div style={{ marginTop: '0.5rem' }}>
        {initial ? (
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-muted)' }}>{initial}</p>
        ) : (
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-muted)', fontStyle: 'italic' }}>
            Sin notas.
          </p>
        )}
        {canEdit && (
          <button
            className="btn"
            onClick={() => setEditing(true)}
            style={{ marginTop: '0.4rem', padding: '4px 10px', fontSize: '0.8rem' }}
          >
            {initial ? 'Editar notas' : 'Agregar notas'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <textarea
        className="input"
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Preferencias, observaciones, alertas..."
        autoFocus
      />
      {error && <p className="error">{error}</p>}
      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
        <button
          className="btn btn-primary"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate(draft.trim())}
        >
          {saveMut.isPending ? 'Guardando...' : 'Guardar'}
        </button>
        <button
          className="btn"
          onClick={() => {
            setEditing(false);
            setDraft(initial ?? '');
            setError(null);
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
